import React, { useCallback } from "react";
import { useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { ethers } from "ethers";
import { CardBody, CardRoot, Flex, Link, Stack, Text } from "@chakra-ui/react";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

const ClaimBroadcastModal: React.FC = () => {
  const { transactionDetails, selectedChainId, setTransactionDetails } = useGlobalState();
  const {
    isClaimBroadcastModalVisible,
    setClaimBroadcastModalVisible,
  } = useModalState();

  // Function to close the modal
  const handleClose = useCallback(() => {
    setClaimBroadcastModalVisible(false);
    setTransactionDetails(null);
  }, [setClaimBroadcastModalVisible, setTransactionDetails]);

  // The claim tx is broadcast asynchronously; only show the modal once the
  // transaction details (including the tx hash) are available.
  if (!isClaimBroadcastModalVisible || !transactionDetails?.tx) {
    return null;
  }

  // Safely format transaction details
  const formattedTransactionId = transactionDetails?.tx
    ? `${transactionDetails.tx.slice(0, 6)}...${transactionDetails.tx.slice(-4)}`
    : "";

  const bigIntMorValue = BigInt(transactionDetails?.morAmount ?? 0)

  const morValueInUSD = parseFloat(ethers.formatEther(bigIntMorValue) ?? '0') * (transactionDetails?.morPrice);
  const ethFeeInUSD = (transactionDetails?.ethFee ?? 0) * (transactionDetails?.ethPrice ?? 0);

  return (
    <DialogRoot open={isClaimBroadcastModalVisible} onOpenChange={(e) => { if (!e.open) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transaction Sent</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Text>
            Your transaction has been submitted. It may take a few minutes for
            your account to reflect any changes. You can view the status of
            your transaction by clicking the transaction ID below, or close
            this window.
          </Text>
          <CardRoot variant='subtle' mt={8}>
            <CardBody>
              <Stack>
                <Flex justifyContent="space-between">
                  <Text color='fg.muted'>Amount Claimed:</Text>
                  <Text>
                    {(parseFloat(ethers.formatEther(transactionDetails?.morAmount.toFixed(0)) ?? 0)).toFixed(3)} MOR (${morValueInUSD.toFixed(2)})
                  </Text>
                </Flex>
                <Flex justifyContent="space-between">
                  <Text color='fg.muted'>Fee:</Text>
                  <Text>
                    {(transactionDetails?.ethFee ?? 0).toFixed(8)} ETH (${ethFeeInUSD.toFixed(2)})
                  </Text>
                </Flex>
                <Flex justifyContent="space-between">
                  <Text color='fg.muted'>Transaction ID:</Text>
                  <Link
                    href={
                      selectedChainId === 42161
                        ? `https://arbiscan.io/tx/${transactionDetails?.tx}`
                        : `https://basescan.org/tx/${transactionDetails?.tx}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {formattedTransactionId}
                  </Link>
                </Flex>
              </Stack>
            </CardBody>
          </CardRoot>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ClaimBroadcastModal;
